/**
 * Confidence Intervals for Composite Pillar Scores
 *
 * Most dashboards show scores without indicating reliability. When a user
 * has 3 of 7 days of data, the score is less reliable — but looks just as
 * definitive. Displaying uncertainty prevents overreacting to noisy data
 * and builds clinical trust (BMJ Open, 2023).
 */

export type ConfidenceLabel = 'High confidence' | 'Moderate confidence' | 'Low confidence' | 'Insufficient data';

export interface ConfidenceResult {
  /** 0–1 (1 = highest confidence). */
  confidence: number;
  /** Human-readable label. */
  label: ConfidenceLabel;
  /** Whether to display the ± interval visually (true if confidence < 0.8). */
  showInterval: boolean;
}

export interface ScoreInterval {
  lower: number;
  upper: number;
  margin: number;
}

/**
 * Calculate a confidence score (0–1) for a pillar's composite score
 * based on data completeness, recency, and source diversity.
 *
 * Factors:
 * 1. Completeness: fraction of expected data points available
 * 2. Recency: exponential decay weighting — recent data is more reliable
 * 3. Source diversity: multiple sources = more reliable
 */
export function calcConfidence(params: {
  dataPointsAvailable: number;
  dataPointsExpected: number;
  newestDataDaysAgo: number;
  sourceCount: number;
}): ConfidenceResult {
  const { dataPointsAvailable, dataPointsExpected, newestDataDaysAgo, sourceCount } = params;

  if (dataPointsExpected <= 0 || dataPointsAvailable <= 0) {
    return { confidence: 0, label: 'Insufficient data', showInterval: true };
  }

  // Factor 1: Completeness (0–1)
  const completeness = Math.min(1, dataPointsAvailable / dataPointsExpected);

  // Factor 2: Recency — exponential decay, weight = e^(-0.05 * daysAgo)
  const recency = Math.exp(-0.05 * Math.max(0, newestDataDaysAgo));

  // Factor 3: Source diversity bonus (1 source = 0.85, 2+ = 1.0)
  const diversity = sourceCount >= 2 ? 1.0 : 0.85;

  // Weighted combination: completeness is most important (60%), recency (30%), diversity (10%)
  const confidence = Math.min(1, completeness * 0.6 + recency * 0.3 + diversity * 0.1);
  const rounded = Math.round(confidence * 100) / 100;

  const label: ConfidenceLabel =
    rounded >= 0.8 ? 'High confidence' :
    rounded >= 0.5 ? 'Moderate confidence' :
    rounded >= 0.2 ? 'Low confidence' :
    'Insufficient data';

  return { confidence: rounded, label, showInterval: rounded < 0.8 };
}

/**
 * Calculate the display interval for a pillar score given its confidence.
 * Higher confidence = narrower interval.
 *
 * Formula: margin = baseScore * (1 - confidence) * scaleFactor
 *   scaleFactor = 0.15 (at 0 confidence, ±15% of the score)
 *
 * Lower/upper clamped to 0–100.
 */
export function calcScoreInterval(score: number, confidence: number): ScoreInterval {
  if (confidence >= 1) return { lower: score, upper: score, margin: 0 };

  const margin = Math.round(score * (1 - confidence) * 0.15);
  const lower = Math.max(0, score - margin);
  const upper = Math.min(100, score + margin);

  return { lower, upper, margin };
}
